'use client';

import { Box, BoxProps, Button, ButtonProps, CircularProgress, Grid } from '@mui/material';
import { isEmptyValue } from '@repo/utils';
import React, { SubmitEvent, useEffect, useRef, useState } from 'react';
import { MOTION } from '../../constants';
import { FadeInUp } from '../fadeInUp';

// Each field enters 60ms after the previous one, like customer-mobile form screens; `&&` outranks the theme's own field stagger
const FIELDS_ENTRANCE = {
  '&& > *': {
    animation: `directoFadeInUp ${MOTION.medium}ms ${MOTION.easeOut} backwards`,
    ...Object.fromEntries(
      Array.from({ length: MOTION.maxStaggerIndex }, (_, index) => [
        `&:nth-child(${index + 2})`,
        { animationDelay: `${(index + 1) * MOTION.stagger}ms` },
      ]),
    ),
    '&:nth-child(n + 10)': { animationDelay: `${MOTION.maxStaggerIndex * MOTION.stagger}ms` },
  },
  '& > * .MuiFormControl-root': { animation: 'none' },
};

const FOCUSABLE = 'input:not([type="hidden"]), select, textarea, button, [tabindex]:not([tabindex="-1"])';

/**
 * Renders a generic form component with a button and no submit logic.
 *
 * @param {Object} formProps - The props for the Form component.
 * @param {Object} formProps.sx - The custom styles for the Form component.
 * @param {ReactNode} formProps.children - The children of the Form component.
 * @param {Function} formProps.onSubmit - The submit handler for the form.
 * @param {Object} [buttonProps][] - The props for the Button components.
 * @param {string} [buttonProps.children='continuar'] - The label for the Button component.
 * @return {React.JSX.Element} The rendered Form component.
 */
export const Form = ({
  submitting,
  formProps: { sx, children: formChildren, onSubmit, ...formProps },
  buttonProps = [{ children: 'continuar' }],
}: {
  submitting?: boolean;
  formProps: BoxProps;
  buttonProps?: ButtonProps[];
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [invalidSubmits, setInvalidSubmits] = useState(0);

  // After an invalid submit, move focus to the first field flagged as invalid
  useEffect(() => {
    if (!invalidSubmits) return;
    const frame = requestAnimationFrame(() => {
      const invalid = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
      const target = invalid?.matches(FOCUSABLE) ? invalid : invalid?.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [invalidSubmits]);

  // Buttons enter right after the last field
  const fieldCount = React.Children.toArray(formChildren).length;

  const formHasError = (formChildren: React.ReactNode): any => {
    const result: any = React.Children.map(formChildren, (child: React.ReactNode) => {
      if (React.isValidElement(child)) {
        const props = child.props as any;
        if (props.children && !props.error) {
          return formHasError(props.children);
        }
        // This blur function highlight errors on form fields
        props?.onBlur?.();
        // Conditional that prevents the form submit if a required field is empty
        if (props?.required && isEmptyValue(props?.value)) {
          return true;
        }
        // Conditional that prevents the form submit if a field has a visual error or an internal error
        if (props.error || props['data-internalerror']) {
          return true;
        }
      }
    });
    // if (result?.[0] === true) {
    if (result?.includes(true)) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <form
      ref={formRef}
      noValidate
      data-testid="form"
      aria-busy={submitting || undefined}
      onSubmit={(e: SubmitEvent<any>) => {
        e.preventDefault();
        if (submitting) return;
        if (formHasError(formChildren)) {
          setInvalidSubmits((count) => count + 1);
        } else {
          onSubmit?.(e);
        }
      }}
      style={{ height: '100%', width: '100%' }}
    >
      <Box
        {...formProps}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'space-between',
          paddingBottom: { xs: 4, md: 0 },
          gap: 2,
          pointerEvents: submitting ? 'none' : 'auto',
          filter: submitting ? 'grayscale(100%)' : 'none',
          ...sx,
        }}
      >
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...FIELDS_ENTRANCE }}>{formChildren}</Grid>
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          {buttonProps.map(({ children, name, disabled, id, ...rest }, index) => (
            <FadeInUp key={index} index={fieldCount + index}>
              <Button
                fullWidth
                id={id || `button-${name}`}
                data-testid={`button-${name}`}
                disabled={submitting || disabled}
                type={index === 0 ? 'submit' : 'button'}
                variant={index === 0 ? 'contained' : 'inverted'}
                children={
                  index === 0 && submitting ? (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={20} aria-hidden="true" />
                      Cargando...
                    </Box>
                  ) : (
                    <span>{children}</span>
                  )
                }
                {...rest}
                // After the spread so a caller's onClick can't skip the submit button's field check
                onClick={(e) => {
                  if (index === 0) formHasError(formChildren);
                  rest?.onClick?.(e);
                }}
              />
            </FadeInUp>
          ))}
        </Grid>
      </Box>
    </form>
  );
};
